import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import { Divider, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import {
    CAMPO_FONTE_DADOS_TIPO,
    CAMPO_TIPO_DADO,
    CampoFonteDadosEnum,
    COMPARACAO_TIPO,
    FONTE_DADOS_TIPO
} from '../../regra.types'
import CondicaoFormScope from '../condicao_form.scopes'

const LOG = Logger.get('CONDICAO-FORM-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type CondicaoFormViewProps = {
    className?: string
    scope: CondicaoFormScope
}
class CondicaoFormViewClass implements FCClassContext<CondicaoFormViewProps> {
    // :: Fields
    scope!: CondicaoFormScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    #handleFonteDadosChange = (e: SelectChangeEvent) => {
        this.scope.fonteDados = e.target.value as string
        this.scope.campoFonteDados = '' as CampoFonteDadosEnum
    }

    #handleCampoFonteDadosChange = (e: SelectChangeEvent) => {
        this.scope.campoFonteDados = e.target.value as CampoFonteDadosEnum
    }

    #handleComparacaoChange = (e: SelectChangeEvent) => {
        this.scope.comparacao = e.target.value as string
    }

    #handleTipoChange = (e: SelectChangeEvent) => {
        this.scope.tipo = e.target.value as 'E' | 'OU'
    }

    // :: Methods

    getValoresField = () => {
        const classes = this.sytles.classes
        const scope = this.scope

        if (!scope.campoFonteDados)
            return <TextField className={classes.cnpjInput} disabled sx={{ minWidth: 150 }} variant="standard" />

        const tipoDado = CAMPO_TIPO_DADO[scope.campoFonteDados]

        if (tipoDado.opcoes && tipoDado.tipo === 'opcao') {
            return (
                <>
                    <FormControl>
                        <InputLabel id="valor-select-label">Valor</InputLabel>
                        <Select
                            labelId="valor-select-label"
                            id="valor-select"
                            value={scope.valor1}
                            label="Valor"
                            sx={{ minWidth: 150 }}
                            onChange={(e) => (this.scope.valor1 = e.target.value as string)}
                        >
                            {tipoDado.opcoes.map((opcao) => (
                                <MenuItem key={opcao.valor} value={opcao.valor}>
                                    {opcao.label ?? opcao.valor}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )
        } else if (tipoDado.tipo === 'cfop') {
            return (
                <>
                    <FormControl>
                        <InputLabel id="valor-select-label">Valor</InputLabel>
                        <Select
                            labelId="valor-select-label"
                            id="valor-select"
                            value={scope.valor1}
                            label="Valor"
                            sx={{ minWidth: 150 }}
                            onChange={(e) => (this.scope.valor1 = e.target.value as string)}
                        >
                            {this.scope.cfops.map((cfop) => (
                                <MenuItem key={cfop.id} value={cfop.cfop}>
                                    {`${cfop.cfop} - ${cfop.descricao}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )
        }

        return (
            <>
                <TextField
                    className={classes.cnpjInput}
                    type={tipoDado.tipo === 'numero' ? 'number' : 'text'}
                    value={scope.valor1}
                    onChange={(e) => (scope.valor1 = e.target.value)}
                    label={scope.comparacao === 'ENTRE' ? 'Valor 1' : 'Valor'}
                    sx={{ minWidth: 150 }}
                    variant="standard"
                />

                {scope.comparacao === 'ENTRE' && (
                    <TextField
                        className={classes.cnpjInput}
                        type="number"
                        value={scope.valor2}
                        onChange={(e) => (scope.valor2 = e.target.value)}
                        label="Valor 2"
                        variant="standard"
                    />
                )}
            </>
        )
    }

    onSyncState({ scope }: CondicaoFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: CondicaoFormViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px" marginBottom="20px">
                        <FormControl>
                            <InputLabel id="fonte-dados-select-label">Fonte Dados</InputLabel>
                            <Select
                                labelId="fonte-dados-select-label"
                                id="fonte-dados-select"
                                value={scope.fonteDados}
                                label="Fonte Dados"
                                sx={{ minWidth: 150 }}
                                onChange={this.#handleFonteDadosChange}
                            >
                                {Array.from(FONTE_DADOS_TIPO)
                                    .filter((fonte) => Array.from(CAMPO_FONTE_DADOS_TIPO[fonte[0]]).length > 0)
                                    .map(([key, value]) => (
                                        <MenuItem key={key} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel id="campo-fonte-dados-select-label">Campo</InputLabel>
                            <Select
                                labelId="campo-fonte-dados-select-label"
                                id="campo-fonte-dados-select"
                                value={scope.campoFonteDados}
                                label="Campo"
                                sx={{ minWidth: 150 }}
                                onChange={this.#handleCampoFonteDadosChange}
                            >
                                {Array.from(CAMPO_FONTE_DADOS_TIPO[this.scope.fonteDados]).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel id="comparacao-select-label">Comparação</InputLabel>
                            <Select
                                labelId="comparacao-select-label"
                                id="comparacao-select"
                                value={scope.comparacao}
                                label="Comparação"
                                sx={{ minWidth: 150 }}
                                onChange={this.#handleComparacaoChange}
                            >
                                {Array.from(COMPARACAO_TIPO).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel id="tipo-select-label">Tipo</InputLabel>
                            <Select
                                labelId="tipo-select-label"
                                id="tipo-select"
                                value={scope.tipo}
                                label="Comparação"
                                sx={{ minWidth: 80 }}
                                onChange={this.#handleTipoChange}
                            >
                                <MenuItem value={'E'}>E</MenuItem>
                                <MenuItem value={'OU'}>OU</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px" marginBottom="20px">
                        {this.getValoresField()}
                    </Box>

                    <Divider />

                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px" marginTop="10px">
                        <Button variant="outlined" onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Voltar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const CondicaoFormView = classToFComponent(CondicaoFormViewClass, React)
export default CondicaoFormView
